// arbeitspakete_gantt — one bar per deliverable over equal month columns (v15 slide 6:
// label column to x382, track x382–1216, rows on a 25.8px pitch from y204, bars 11px high).
// Bars and labels come pre-computed from the bound resolver (leftPct/widthPct over the track).
import type { ArbeitspaketeGanttContent, GanttBound } from '../../../../shared/deckTypes.js';
import { Abs, Brand, Dkp, Eyebrow, PageNo, PHASE_FILL, PHASE_TEXT, Rule } from './chrome.js';

const TRACK_X = 382;
const TRACK_W = 834;
const ROW_Y0 = 204;
const ROW_PITCH = 25.8;

export default function ArbeitspaketeGanttPreview({ content, bound, pageNo }: {
  content: ArbeitspaketeGanttContent;
  bound?: GanttBound;
  pageNo?: number | null;
}) {
  const c = content;
  const rows = bound?.rows ?? [];
  const months = bound?.months ?? [];
  const colW = months.length > 0 ? TRACK_W / months.length : TRACK_W;
  const bodyH = Math.max(1, rows.length) * ROW_PITCH + 12;
  const legend = (c.phase_labels ?? []).slice(0, 4);
  const markers = bound?.markers ?? [];

  return (
    <Dkp>
      <Eyebrow text={c.eyebrow ?? ''} />
      <Abs x={64} y={84} w={1000}>
        <div className="dkp-title">{c.title}</div>
      </Abs>

      {/* month axis: labels + hairline columns, closed by the ink rule at y190 */}
      {months.map((m, i) => {
        const x = TRACK_X + i * colW;
        return (
          <div key={i}>
            <Abs x={x} y={168} w={1} h={22} style={{ background: '#D9D6CF' }} />
            <Abs x={x + 8} y={168} w={colW - 8}>
              <span className="dkp-th">{m.label}</span>
            </Abs>
            <Abs x={x} y={198} w={1} h={bodyH} style={{ background: '#D9D6CF' }} />
          </div>
        );
      })}
      <Rule x={64} y={190} w={1152} color="#121316" />
      {c.intro ? (
        <Abs x={64} y={130} w={1000}>
          <div style={{ fontSize: 13.3, fontWeight: 500, color: '#3A3D42' }}>{c.intro}</div>
        </Abs>
      ) : null}

      {/* date markers: dashed gold verticals with a mono label (v15: GF / PrK dates) */}
      {markers.map((m, i) => {
        const x = TRACK_X + (m.leftPct / 100) * TRACK_W;
        return (
          <div key={i}>
            <Abs x={x} y={198} w={0} h={416} style={{ borderLeft: '1px dashed #C8A96E' }} />
            <Abs x={x + 4} y={198} w={120}>
              <span className="dkp-mono" style={{ fontSize: 8.5, color: '#8A6F3E', whiteSpace: 'nowrap' }}>{m.label}</span>
            </Abs>
          </div>
        );
      })}

      {/* one row per deliverable: mono ref+name in the phase's text colour, bar on the track */}
      {rows.map((r, i) => {
        const y = ROW_Y0 + i * ROW_PITCH;
        const phase = Math.max(0, Math.min(PHASE_FILL.length - 1, r.phaseIndex));
        const barX = TRACK_X + (r.leftPct / 100) * TRACK_W;
        const barW = Math.max(3, (r.widthPct / 100) * TRACK_W);
        return (
          <div key={r.ref}>
            <Abs x={64} y={y} w={310}>
              <span className="dkp-mono" style={{
                fontSize: 9.3, fontWeight: 700, color: PHASE_TEXT[phase],
                whiteSpace: 'nowrap', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis',
              }}>
                {r.ref}&nbsp;&nbsp;{r.name}
              </span>
            </Abs>
            <Abs x={barX} y={y + 1} w={barW} h={11} style={{
              background: PHASE_FILL[phase], borderRadius: 2,
            }} />
          </div>
        );
      })}

      {/* phase legend (slot), bottom-left band */}
      {legend.length > 0 ? (
        <Abs x={64} y={648} w={1152} style={{ display: 'flex', gap: 26, alignItems: 'center' }}>
          {legend.map((label, i) => (
            <span key={i} style={{ display: 'inline-flex', gap: 7, alignItems: 'center' }}>
              <span style={{ width: 9, height: 9, background: PHASE_FILL[i % PHASE_FILL.length], flex: 'none' }} />
              <span className="dkp-th" style={{ color: PHASE_TEXT[i % PHASE_TEXT.length] }}>{label}</span>
            </span>
          ))}
        </Abs>
      ) : null}

      <Brand />
      <PageNo n={pageNo} />
    </Dkp>
  );
}
